import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GuidedTourService } from '../guided-tour.service';
import { Tutorial } from '../tutorial.types';

/**
 * Chip "¿Cómo funciona esto?" — la entrada MANUAL y discreta a un recorrido
 * guiado, DENTRO de la pantalla que ese recorrido explica (§20 T2).
 *
 * Nace de generalizar los dos chips que ya existían a mano (el del panel de
 * búsqueda global y el de la modal de resultados): eran los únicos recorridos
 * con una puerta visible en su propia área; el resto solo se podía alcanzar por
 * la píldora de invitación (que caduca a los 7 días) o por el encadenado. Si te
 * perdiste esa ventana, el tutorial existía pero no había dónde pedirlo.
 *
 * Reglas de la pieza:
 *  - Solo se pinta si el recorrido sigue PENDIENTE (`isPending`, que ya respeta
 *    el apagado por admin/usuario y el "no mostrar más"). Nunca insiste.
 *  - Acepta una LISTA en orden y ofrece el primero pendiente: una cadena entera
 *    (el grafo son seis recorridos) se cubre con un chip, no con seis.
 *  - Un recorrido PAUSADO no se ofrece aquí — de eso se encarga el botón de
 *    "Retomar", que además lo reanuda donde quedó.
 */
// Angular 9: sin `standalone`; lo declara y exporta GuidedTourModule.
@Component({
  selector: 'app-tutorial-hint',
  template: `
    <button
      *ngIf="pendiente as t"
      type="button"
      class="gt-chip"
      [class.gt-chip--floating]="tone === 'floating'"
      (click)="iniciar()"
      [attr.title]="
        (pausado ? 'Retomar donde lo dejaste: ' : 'Ver un recorrido guiado: ') +
        t.title
      "
    >
      <span class="gt-chip__icon" aria-hidden="true">?</span>
      {{ pausado ? labelPausado : label }} ›
    </button>
  `,
  styles: [
    `
      :host {
        display: inline-flex;
      }

      .gt-chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border: 1px solid rgba(26, 140, 216, 0.35);
        border-radius: 999px;
        background: rgba(26, 140, 216, 0.08);
        color: #1a8cd8;
        font-family: inherit;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.2;
        white-space: nowrap;
        cursor: pointer;
        transition: background 0.15s ease;
      }

      .gt-chip:hover {
        background: rgba(26, 140, 216, 0.16);
      }

      /* Sobre un lienzo o un fondo con contenido: fondo sólido + sombra para no
         confundirse con los nodos del grafo. */
      .gt-chip--floating {
        background: #ffffff;
        box-shadow: 0 2px 10px rgba(15, 23, 42, 0.14);
      }

      .gt-chip--floating:hover {
        background: rgba(26, 140, 216, 0.1);
      }

      .gt-chip__icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: none;
        width: 15px;
        height: 15px;
        border-radius: 50%;
        background: #1a8cd8;
        color: #fff;
        font-size: 10px;
        font-weight: 800;
        line-height: 1;
      }
    `,
  ],
})
export class TutorialHintComponent implements OnInit, OnDestroy {
  /**
   * Tutoriales candidatos EN ORDEN (id o lista de ids). Se ofrece el primero
   * que siga pendiente.
   */
  @Input() tutorials: string | string[] = [];

  /** Texto del chip. El título del recorrido va siempre en el `title`. */
  @Input() label = '¿Cómo funciona esto?';

  /** Texto cuando el recorrido está PAUSADO (se retoma donde quedó). */
  @Input() labelPausado = 'Retomar el recorrido';

  /** `floating` = fondo sólido, para chips que van sobre el lienzo del grafo. */
  @Input() tone: 'inline' | 'floating' = 'inline';

  pendiente: Tutorial | null = null;
  /** El recorrido ofrecido quedó a medias: el clic lo RETOMA, no lo reinicia. */
  pausado = false;

  private sub = new Subscription();

  constructor(private tour: GuidedTourService) {}

  ngOnInit(): void {
    this.refresh();
    // El chip cambia con: el progreso (completar uno destapa el siguiente de la
    // cadena), el estado local (pausar / interruptor del usuario), el
    // interruptor del admin, y el arranque/cierre de un recorrido.
    this.sub.add(this.tour.observeProgress().subscribe(() => this.refresh()));
    this.sub.add(this.tour.observeLocal().subscribe(() => this.refresh()));
    this.sub.add(
      this.tour.observeAdminEnabled().subscribe(() => this.refresh()),
    );
    this.sub.add(this.tour.observe().subscribe(() => this.refresh()));
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  /** El usuario PIDE el recorrido: es el único camino por el que arranca. */
  iniciar(): void {
    const t = this.pendiente;
    if (!t) return;
    const retomar = this.pausado;
    this.pendiente = null;
    if (retomar) this.tour.resume(t.id);
    else this.tour.start(t.id);
  }

  private refresh(): void {
    // Con un recorrido en marcha el chip sobra: competiría con la tarjeta. Y con
    // el asistente apagado (admin u usuario) no se ofrece nada — el motor no
    // lanzaría el recorrido de todas formas.
    if (this.tour.isActive() || this.tour.areTutorialsOff()) {
      this.pendiente = null;
      this.pausado = false;
      return;
    }
    const ids = (
      Array.isArray(this.tutorials) ? this.tutorials : [this.tutorials]
    ).filter((i) => !!i);
    // Un recorrido dejado a medias tiene prioridad sobre uno sin empezar: lo que
    // el usuario quiere es terminar lo que ya venía haciendo.
    const pausadoId = ids.find(
      (i) => this.tour.isPaused(i) && !this.tour.isDisabled(i),
    );
    const id = pausadoId ?? ids.find((i) => this.tour.isPending(i));
    this.pausado = !!pausadoId;
    this.pendiente = id ? this.tour.getById(id) ?? null : null;
  }
}
